import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';

import { authService } from '@/services/auth';
import { useAuthStore } from '@/stores/authStore';
import { tenantManager } from '@/utils/tenantManager';

export const useAuth = () => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { user, accessToken, setAuth, clearAuth } = useAuthStore();
  const [remember, setRemember] = useState(useAuthStore.getState().remember);

  const loginMutation = useMutation({
    mutationFn: authService.login,
    onSuccess: (data) => {
      setAuth({ user: data.user, accessToken: data.accessToken ?? '' }, remember);
      toast.success(`Bem-vindo(a), ${data.user.name}`);
      navigate('/app');
    },
    onError: (error: any) => {
      // Tratamento específico para erros de rede
      if (error?.isNetworkError || !error?.response) {
        const message = error?.message || 'Erro de conexão. Verifique sua internet e tente novamente.';
        console.error('Login network error:', {
          message: error?.message,
          code: error?.code,
          error,
        });
        toast.error(message);
        return;
      }

      const message =
        error?.response?.data?.message ||
        error?.response?.data?.code ||
        error?.message ||
        'Credenciais inválidas';
      console.error('Login error:', {
        message,
        status: error?.response?.status,
        data: error?.response?.data,
        error,
      });
      toast.error(message);
    },
  });

  const registerMutation = useMutation({
    mutationFn: authService.register,
    onSuccess: (_data) => {
      toast.success('Cadastro realizado! Faça login para acessar o sistema.', { duration: 5000 });
    },
    onError: (error: any) => {
      const message =
        error?.response?.data?.message || 'Erro ao criar conta. Tente novamente.';
      toast.error(message);
    },
  });

  const logoutMutation = useMutation({
    mutationFn: authService.logout,
    onSuccess: () => {
      tenantManager.clear();
      clearAuth();
      queryClient.clear();
      navigate('/');
    },
    onError: () => {
      tenantManager.clear();
      clearAuth();
      queryClient.clear();
      navigate('/');
    },
  });

  const meQuery = useQuery({
    queryKey: ['me'],
    queryFn: authService.me,
    enabled: Boolean(accessToken && !user),
    retry: false,
  });

  // Atualizar auth quando meQuery retornar dados
  useEffect(() => {
    if (meQuery.data) {
      setAuth({ user: meQuery.data, accessToken: accessToken ?? '' }, remember);
    }
  }, [meQuery.data, accessToken, remember, setAuth]);

  return {
    user,
    accessToken,
    remember,
    setRemember,
    login: loginMutation.mutateAsync,
    isLoggingIn: loginMutation.isPending,
    register: registerMutation.mutateAsync,
    isRegistering: registerMutation.isPending,
    logout: logoutMutation.mutateAsync,
    isLoadingUser: meQuery.isLoading,
  };
};
